import { inject } from "@angular/core";
import { CanActivateFn, Router } from "@angular/router";
import { appRoute } from "src/app/app-route.constants";
import { canHandOffToReact, reactHomeUrl } from "../utils/home-route.util";
import { isReactAppRoute } from "../utils/myzpax-routes.util";

/**
 * Catch-all for URLs the Angular router does not match. Note this also owns the
 * bare root URL `/`: the `**` route sits inside the empty-path parent and no
 * sibling claims the empty path, so `/` lands here too.
 *
 * A plain `redirectTo` cannot reach the React app (it lives outside the Angular
 * router), so the handoff is a full-page replace — replace rather than assign so
 * the unmatched URL does not stay in history.
 *
 * Two ways this must not hand off:
 *
 * - Angular only sees a `/rc` URL when React is not being served at this origin
 *   (missing edge rewrite, failed or rolled-back React deploy, Angular deployed
 *   ahead of React). Replacing with `/rc/home` would reload straight back into
 *   this guard.
 * - React refuses sessions Angular's guards accept, and answers by sending the
 *   user back here. `canHandOffToReact()` applies React's own rule so the two
 *   cannot disagree, and trips a circuit breaker if they somehow still do.
 *
 * Either way the fallback is the Angular home route, which always renders or
 * triggers a real login.
 */
export const homeFallbackGuard: CanActivateFn = () => {
  const reactAppUnreachable = isReactAppRoute(window.location.pathname);

  if (!reactAppUnreachable && canHandOffToReact()) {
    window.location.replace(reactHomeUrl);
    return false;
  }

  return inject(Router).createUrlTree([`/${appRoute.home}`]);
};
